import { useEffect, useMemo, useState } from 'react';
import { Settings2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import customFieldsApi from '../../api/customFields';

const valueFromResponse = (field) => {
  if (field.field_type === 'multi_select' && field.value_json) {
    return Array.isArray(field.value_json) ? field.value_json.join(', ') : String(field.value_json);
  }
  if (field.field_type === 'number' && field.value_numeric !== null && field.value_numeric !== undefined) {
    return String(field.value_numeric);
  }
  if (field.field_type === 'date' && field.value_date) {
    return field.value_date;
  }
  return field.value || '';
};

const normalizeValue = (value) => String(value ?? '').trim();

export default function CustomFieldsForm({ entityType, entityId, onPayloadChange }) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadCustomFields = async () => {
      if (!entityType || !entityId) return;
      setLoading(true);
      try {
        const response = await customFieldsApi.getValues(entityType, entityId);
        const nextValues = {};
        response.forEach((field) => {
          nextValues[field.field_id] = valueFromResponse(field);
        });
        setFields(response);
        setValues(nextValues);
        setInitialValues(nextValues);
      } catch (error) {
        setFields([]);
        setValues({});
        setInitialValues({});
      } finally {
        setLoading(false);
      }
    };

    loadCustomFields();
  }, [entityType, entityId]);

  const payload = useMemo(() => fields
    .filter((field) => normalizeValue(values[field.field_id]) !== normalizeValue(initialValues[field.field_id]))
    .map((field) => ({
      field_id: field.field_id,
      value: values[field.field_id]?.toString() || ''
    })), [fields, initialValues, values]);

  useEffect(() => {
    onPayloadChange?.(payload);
  }, [payload, onPayloadChange]);

  if (loading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
        Loading configurable fields...
      </div>
    );
  }

  if (fields.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500">
        No configurable fields are defined for this record type yet.
      </div>
    );
  }

  const setFieldValue = (fieldId, value) => {
    setValues((current) => ({ ...current, [fieldId]: value }));
  };

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <Settings2 className="h-4 w-4 text-blue-600" />
        <div>
          <div className="text-sm font-semibold text-slate-900">Configurable Fields</div>
          <div className="text-xs text-slate-500">Workspace-specific fields for this customer account.</div>
        </div>
      </div>
      <div className="grid gap-4 p-4 md:grid-cols-2">
        {fields.map((field) => {
          const value = values[field.field_id] ?? '';
          const options = field.field_options?.values || [];

          return (
            <div key={field.field_id}>
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {field.field_name}{field.is_required ? ' *' : ''}
              </Label>
              <div className="mt-1.5">
                {field.field_type === 'dropdown' && options.length > 0 ? (
                  <select
                    value={value}
                    onChange={(event) => setFieldValue(field.field_id, event.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Not set</option>
                    {options.map((option) => <option key={option} value={option}>{option}</option>)}
                  </select>
                ) : field.field_type === 'checkbox' ? (
                  <label className="flex h-10 items-center gap-2 rounded-md border border-input px-3 text-sm">
                    <input
                      type="checkbox"
                      checked={value === 'true' || value === true}
                      onChange={(event) => setFieldValue(field.field_id, event.target.checked ? 'true' : 'false')}
                    />
                    Yes
                  </label>
                ) : (
                  <Input
                    type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'}
                    value={value}
                    onChange={(event) => setFieldValue(field.field_id, event.target.value)}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
